import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildAddressQuery, geocodeAddress, haversineKm, normalizeCoordinates, normalizeCountryName, parseNumberLoose } from '../utils/helpers'

export const useDeliveryDistance = ({ shippingSettings, address, method, debounceMs = 650, requireConfirmation = true }) => {
  const [originCoords, setOriginCoords] = useState(null)
  const [distance, setDistance] = useState(null)
  const [status, setStatus] = useState({ state: 'idle', message: '' })
  const [confirmedKey, setConfirmedKey] = useState('')
  const lastQueryRef = useRef({ origin: '', dest: '' })
  const requestRef = useRef({ id: 0 })

  const distancePricing = shippingSettings?.distancePricing || {}
  const shouldComputeDistance = useMemo(() => {
    if (method !== 'delivery') return false
    const tiers = Array.isArray(distancePricing?.tiers) ? distancePricing.tiers : []
    const hasTiers = tiers.length > 0
    const freeShippingMaxDistance = Number(shippingSettings?.freeShippingMaxDistance)
    const hasFreeMaxDistance = Number.isFinite(freeShippingMaxDistance) && freeShippingMaxDistance > 0
    const negotiationEnabled = distancePricing?.negotiationEnabled === true || distancePricing?.negotiationEnabled === 'true'
    const negotiationDistance = parseNumberLoose(distancePricing?.negotiationDistance)
    const hasNegotiationDistance = Number.isFinite(negotiationDistance) && negotiationDistance > 0
    return hasTiers || hasFreeMaxDistance || (negotiationEnabled && hasNegotiationDistance)
  }, [distancePricing?.negotiationDistance, distancePricing?.negotiationEnabled, distancePricing?.tiers, method, shippingSettings?.freeShippingMaxDistance])

  const addressKey = useMemo(() => {
    const street = String(address?.street || '').trim().toLowerCase()
    const apartment = String(address?.apartment || '').trim().toLowerCase()
    const city = String(address?.city || '').trim().toLowerCase()
    const postalCode = String(address?.postalCode || '').trim().toLowerCase()
    const province = String(address?.province || '').trim().toLowerCase()
    const country = String(normalizeCountryName(address?.country) || '').trim().toLowerCase()
    return [street, apartment, city, postalCode, province, country].join('|')
  }, [address?.apartment, address?.city, address?.country, address?.postalCode, address?.province, address?.street])

  const isAddressComplete = useMemo(() => {
    const street = String(address?.street || '').trim()
    const city = String(address?.city || '').trim()
    const province = String(address?.province || '').trim()
    const country = String(normalizeCountryName(address?.country) || '').trim()
    return !!street && !!city && !!province && !!country
  }, [address?.city, address?.country, address?.province, address?.street])

  useEffect(() => {
    const originFromSettings = normalizeCoordinates(distancePricing?.originCoordinates)
    if (originFromSettings) {
      setOriginCoords(originFromSettings)
      return
    }
    const originQuery = buildAddressQuery(distancePricing?.originAddress)
    if (!originQuery) {
      setOriginCoords(null)
      return
    }
    let cancelled = false
    const run = async () => {
      const geocoded = await geocodeAddress(originQuery)
      if (cancelled) return
      setOriginCoords(geocoded ? { lat: geocoded.lat, lng: geocoded.lng } : null)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [distancePricing?.originAddress, distancePricing?.originCoordinates])

  useEffect(() => {
    if (method !== 'delivery') {
      setConfirmedKey('')
    }
  }, [method])

  useEffect(() => {
    if (!confirmedKey) return
    if (confirmedKey !== addressKey) {
      setConfirmedKey('')
    }
  }, [addressKey, confirmedKey])

  const confirm = useCallback(() => {
    if (method !== 'delivery') return false
    if (!isAddressComplete) {
      setStatus({ state: 'needs_address', message: 'Completá calle, ciudad, provincia y país para calcular el envío' })
      return false
    }
    lastQueryRef.current = { origin: '', dest: '' }
    requestRef.current.id += 1
    setDistance(null)
    setConfirmedKey(addressKey)
    if (!shouldComputeDistance) {
      setStatus({ state: 'ready', message: '' })
      return true
    }
    if (!originCoords) {
      setStatus({ state: 'error', message: 'No se pudo calcular el envío (origen sin configurar)' })
      return true
    }
    setStatus({ state: 'loading', message: 'Estamos calculando tu costo de envío…' })
    return true
  }, [addressKey, isAddressComplete, method, originCoords, shouldComputeDistance])

  useEffect(() => {
    if (method !== 'delivery') {
      setDistance(null)
      setStatus({ state: 'idle', message: '' })
      return
    }
    if (!isAddressComplete) {
      setDistance(null)
      setStatus({ state: 'needs_address', message: 'Completá calle, ciudad, provincia y país para calcular el envío' })
      return
    }
    if (requireConfirmation && confirmedKey !== addressKey) {
      setDistance(null)
      setStatus({ state: 'needs_confirmation', message: 'Confirmá tu dirección de entrega para calcular el envío' })
      return
    }
    if (!shouldComputeDistance) {
      setDistance(null)
      setStatus({ state: 'ready', message: '' })
      return
    }
    if (!originCoords) {
      setDistance(null)
      setStatus({ state: 'error', message: 'No se pudo calcular el envío (origen sin configurar)' })
      return
    }
    const destQuery = buildAddressQuery(address)
    const originKey = `${originCoords.lat},${originCoords.lng}`
    if (lastQueryRef.current.origin === originKey && lastQueryRef.current.dest === destQuery) {
      return
    }
    lastQueryRef.current = { origin: originKey, dest: destQuery }

    const currentRequestId = ++requestRef.current.id
    setStatus({ state: 'loading', message: 'Estamos calculando tu costo de envío…' })

    const timer = setTimeout(async () => {
      const destCoords = await geocodeAddress(destQuery)
      if (requestRef.current.id !== currentRequestId) return
      if (!destCoords) {
        setDistance(null)
        setStatus({ state: 'error', message: 'No se pudo calcular el envío con esa dirección' })
        return
      }
      const nextDistance = haversineKm(originCoords, destCoords)
      if (!Number.isFinite(nextDistance)) {
        setDistance(null)
        setStatus({ state: 'error', message: 'No se pudo calcular el envío con esa dirección' })
        return
      }
      setDistance(nextDistance)
      setStatus({ state: 'ready', message: '' })
    }, Math.max(0, debounceMs))

    return () => clearTimeout(timer)
  }, [address, addressKey, confirmedKey, debounceMs, isAddressComplete, method, originCoords, requireConfirmation, shouldComputeDistance])

  const isConfirmed = method === 'delivery' ? !requireConfirmation || (confirmedKey === addressKey && !!confirmedKey) : true
  const canConfirm = method === 'delivery' && isAddressComplete && status.state !== 'loading'

  return { distance, status, originCoords, shouldComputeDistance, confirm, isConfirmed, canConfirm }
}

export default useDeliveryDistance
